import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

/** One pane of a drawer that has more than one. `id` is what the listing switches on. */
export interface DetailDrawerTab {
    id: string;
    label: string;
}

/**
 * The side panel a listing opens when a row is picked, beside the table rather than over it.
 *
 * Chrome only: the sticky card, the title block, the pager through the rows already loaded, the
 * tab strip, and the close. What the panel says about the row is projected in, because that is
 * the only part that differs between one listing and the next.
 *
 * The strip is chrome but the selection is not: a listing decides what its panes are and which
 * one is open, so `tab` comes in and a press goes back out. The strip is drawn only where there
 * is more than one pane, so a single-pane drawer costs nothing.
 *
 * The Alerts page has its own drawer and is deliberately left on it — its panes each fire their
 * own request and carry their own loading and error states, which this does not model.
 */
@Component({
    selector: 'app-detail-drawer',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './detail-drawer.component.html',
    styleUrls: ['./detail-drawer.component.scss'],
})
export class DetailDrawerComponent {
    /** The kind of thing on show, over the title. "Share Detail", "Agent Detail". */
    @Input() eyebrow = '';

    /** What the row is called. */
    @Input() title = '';

    /** Render the title in monospace - for a title that is an address or endpoint, not an entity name. */
    @Input() titleMono = false;

    /** A muted, display-only suffix shown after the title (e.g. ":3389"), so an endpoint reads as its
     *  address with the port set apart rather than as one run-on string. */
    @Input() titleTail = '';

    /** Where the open row sits in the loaded list, and how many there are. */
    @Input() index = -1;
    @Input() total = 0;

    /** The panes on offer. Empty, or one, draws no strip. */
    @Input() tabs: DetailDrawerTab[] = [];

    /** Which pane is open, by id. Owned by the listing, because the body is too. */
    @Input() tab = '';

    /** Set by a listing that opens the drawer beside its table: the drawer's top and frame then match the table's. */
    @Input({ transform: booleanAttribute }) besideList = false;

    @Output() closed = new EventEmitter<void>();

    /** -1 for the row above, 1 for the row below. The page decides what that means. */
    @Output() navigated = new EventEmitter<-1 | 1>();

    /** The id of the pane just pressed. Nothing is switched here. */
    @Output() tabChange = new EventEmitter<string>();

    get hasPrev(): boolean {
        return this.index > 0;
    }

    get hasNext(): boolean {
        return this.index >= 0 && this.index < this.total - 1;
    }

    /** Drawn only where there is somewhere to page to. */
    get showPager(): boolean {
        return this.total > 1 && this.index >= 0;
    }

    /** A strip of one tab is a label, not a choice. */
    get showTabs(): boolean {
        return this.tabs.length > 1;
    }

    /**
     * Escape closes, from anywhere on the page. A panel opened by a click on a row is dismissed
     * the way every other overlay in this console is, and the close button is the only other way
     * out of it.
     */
    @HostListener('document:keydown.escape')
    onEscape() {
        this.close();
    }

    close() {
        this.closed.emit();
    }

    selectTab(id: string) {
        if (id !== this.tab) {
            this.tabChange.emit(id);
        }
    }

    navigate(step: -1 | 1) {
        if (step === -1 && !this.hasPrev) {
            return;
        }
        if (step === 1 && !this.hasNext) {
            return;
        }
        this.navigated.emit(step);
    }
}
